import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useApp } from '../state/store';

const MIN = 28;
const MAX = 72;

export function useIsNarrow(breakpoint = 900) {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(`(max-width: ${breakpoint}px)`).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(`(max-width: ${breakpoint}px)`);
    if (!mq) return;
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [breakpoint]);
  return narrow;
}

/**
 * Desktop: resizable, keyboard-adjustable split (tutorial left, simulator right), each scrolling independently.
 * Narrow screens: Lesson / Simulator tabs with a persistent status strip.
 */
export function SplitView({ left, right, status }: { left: ReactNode; right: ReactNode; status: ReactNode }) {
  const pct = useApp((s) => s.prefs.splitPercent);
  const setPrefs = useApp((s) => s.setPrefs);
  const narrow = useIsNarrow();
  const [tab, setTab] = useState<'lesson' | 'sim'>('lesson');
  const container = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  useEffect(() => {
    const onGo = () => setTab('sim');
    const onBack = () => setTab('lesson');
    window.addEventListener('app:show-sim', onGo);
    window.addEventListener('app:show-lesson', onBack);
    return () => {
      window.removeEventListener('app:show-sim', onGo);
      window.removeEventListener('app:show-lesson', onBack);
    };
  }, []);

  if (narrow) {
    return (
      <div className="split narrow">
        <div className="tabs" role="tablist" aria-label="Panels">
          <button role="tab" id="tab-lesson" aria-controls="panel-lesson" aria-selected={tab === 'lesson'} className={tab === 'lesson' ? 'on' : ''} onClick={() => setTab('lesson')}>
            Lesson
          </button>
          <button role="tab" id="tab-sim" aria-controls="panel-sim" aria-selected={tab === 'sim'} className={tab === 'sim' ? 'on' : ''} onClick={() => setTab('sim')}>
            Simulator
          </button>
        </div>
        <div className="narrow-status">
          {status}
          {tab === 'sim' && (
            <button type="button" className="btn small ghost" onClick={() => setTab('lesson')}>
              ← Back to instructions
            </button>
          )}
        </div>
        <div id="panel-lesson" role="tabpanel" aria-labelledby="tab-lesson" hidden={tab !== 'lesson'} className="pane">
          {left}
        </div>
        <div id="panel-sim" role="tabpanel" aria-labelledby="tab-sim" hidden={tab !== 'sim'} className="pane">
          {right}
        </div>
      </div>
    );
  }

  const setFromClientX = (clientX: number) => {
    const rect = container.current?.getBoundingClientRect();
    if (!rect) return;
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPrefs({ splitPercent: Math.round(Math.min(MAX, Math.max(MIN, p))) });
  };

  return (
    <div className="split" ref={container} style={{ gridTemplateColumns: `${pct}% 10px 1fr` }}>
      <div className="pane pane-left" role="region" aria-label="Lesson">
        {left}
      </div>
      <div
        className="splitter"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize lesson and simulator panels"
        aria-valuenow={pct}
        aria-valuemin={MIN}
        aria-valuemax={MAX}
        aria-valuetext={`Lesson ${pct}% of the width`}
        tabIndex={0}
        onKeyDown={(e) => {
          const step = e.shiftKey ? 10 : 2;
          if (e.key === 'ArrowLeft') setPrefs({ splitPercent: Math.max(MIN, pct - step) });
          else if (e.key === 'ArrowRight') setPrefs({ splitPercent: Math.min(MAX, pct + step) });
          else if (e.key === 'Home') setPrefs({ splitPercent: MIN });
          else if (e.key === 'End') setPrefs({ splitPercent: MAX });
          else if (e.key === 'Enter') setPrefs({ splitPercent: 45 });
          else return;
          e.preventDefault();
        }}
        onPointerDown={(e) => {
          dragging.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => dragging.current && setFromClientX(e.clientX)}
        onPointerUp={() => (dragging.current = false)}
        onDoubleClick={() => setPrefs({ splitPercent: 45 })}
      />
      <div className="pane pane-right" role="region" aria-label="Simulator">
        {right}
      </div>
    </div>
  );
}
